import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import type { OpenItem } from '@contracts/ledger';
import { useLedgerActions } from '../api/queries';
import { Button } from './Button';
import { Field } from './Field';
import { Modal } from './Overlays';
import { useNotices } from './Notices';
import { initialsOf } from './initials';
import { answersTo } from '../lib/note';

export interface Candidate {
  email: string;
  name: string | null;
  me?: boolean;
}

/**
 * Who owns this? The people who were there, you, or somebody else by address.
 * The app did not guess; a person answers, and the answer is CONFIRMED.
 */
export function OwnerModal({
  item,
  candidates,
  heard,
  onClose,
}: {
  item: Pick<OpenItem, 'id' | 'text' | 'ownerEmail'>;
  candidates: Candidate[];
  /**
   * The name the meeting attributed this to, when no address could be matched
   * to it. Whoever answers to it is put first and said out loud — the person
   * doing this already knows who Artur is, and the app asking them to type an
   * address it could have worked out is the complaint this answers.
   */
  heard?: string | null;
  onClose: () => void;
}): ReactElement {
  const ledger = useLedgerActions();
  const { show } = useNotices();
  const [email, setEmail] = useState('');
  const give = (address: string, name?: string | null) =>
    ledger.assign.mutate(
      { id: item.id, body: { email: address, ...(name ? { name } : {}) } },
      {
        onSuccess: (updated) => {
          onClose();
          show({
            tone: 'success',
            text:
              updated.id === item.id
                ? `Owner set — ${updated.ownerName ?? updated.ownerEmail}.`
                : `Handed over to ${updated.ownerName ?? updated.ownerEmail}. It has left one side of the ledger and appeared on the other.`,
          });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (email.includes('@')) give(email.trim());
  };
  const named = (c: Candidate): boolean => answersTo(heard, c.name, c.email);
  const shown = candidates
    .filter((c) => c.email.toLowerCase() !== item.ownerEmail?.toLowerCase())
    .sort((a, b) => Number(named(b)) - Number(named(a)));
  const suggested = shown.some(named);
  return (
    <Modal title={item.ownerEmail ? 'Hand it over to…' : 'Who owns this?'} onClose={onClose}>
      <p className="t-body">“{item.text}”</p>
      {heard ? (
        <p className="t-secondary">
          The meeting attributed this to <strong>{heard}</strong>
          {suggested
            ? ' — first below, if that is them.'
            : ', and nobody on the invitation answers to that name.'}
        </p>
      ) : null}
      <div className="cluster">
        {shown.map((c) => (
          <button
            key={c.email}
            type="button"
            className={`who ${c.me ? 'me' : ''}`}
            onClick={() => give(c.email, c.name)}
          >
            <span className="avatar">{initialsOf(c.name, c.email)}</span>
            {c.me ? 'Me' : (c.name ?? c.email)}
          </button>
        ))}
      </div>
      <form className="cluster" style={{ flexWrap: 'nowrap' }} onSubmit={submit}>
        <Field
          placeholder="Someone else — their address…"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          type="submit"
          variant="primary"
          working={ledger.assign.isPending ? 'Saving…' : false}
        >
          Give
        </Button>
      </form>
    </Modal>
  );
}
